/**
 * The command palette's search (`components/command-palette.tsx`), pure so it can
 * be tested: what is in the index, how a query ranks it, how the result is
 * grouped, and the recently chosen list it falls back on with no query.
 *
 * Ranking is by tier — exact > prefix > word start > substring > a fuzzy
 * subsequence — case- and diacritic-insensitive, so "aar" finds "Aarna" and
 * "cafe" finds "Café". Ties go to what was chosen more recently, then to the kind
 * (a page before an agent before a board before a card), then to index order.
 */

export type PaletteKind = "page" | "agent" | "board" | "task" | "action";

export interface PaletteItem<T = unknown> {
  /** Stable across sessions: the recent list stores it (`page:status`, `agent:agt_…`). */
  id: string;
  kind: PaletteKind;
  title: string;
  subtitle?: string;
  /** Other words it answers to ("gear", "preferences"); a match here ranks a little below one in the title. */
  keywords: string[];
  /** Shown only when the query matches it, never in the empty list. */
  archived?: boolean;
  /**
   * The score it gets when the query matches neither title nor keywords: a card the
   * server found by its description. Absent means such an item is dropped.
   */
  fallbackScore?: number;
  data: T;
}

export interface Ranked<T = unknown> {
  item: PaletteItem<T>;
  score: number;
  /** Indices into `item.title` that matched, for drawing them bold. */
  positions: number[];
}

export interface PaletteGroup<T = unknown> {
  key: "recent" | PaletteKind;
  label: string;
  results: Ranked<T>[];
}

export const KIND_ORDER: PaletteKind[] = ["page", "agent", "board", "task", "action"];
export const GROUP_LABELS: Record<PaletteGroup["key"], string> = {
  recent: "Recent",
  page: "Pages",
  agent: "Agents",
  board: "Boards",
  task: "Cards",
  action: "Actions",
};
/** How many of each kind a query shows; pages are few enough to show all. */
export const GROUP_CAPS: Record<PaletteKind, number> = { page: Infinity, agent: 6, board: 6, task: 5, action: 5 };
export const RECENT_STORAGE_KEY = "automation.palette.recent";
export const RECENT_LIMIT = 20;
/** Recent rows shown above the pages with no query. */
export const RECENT_SHOWN = 6;

const TIER = { exact: 5, prefix: 4, word: 3, substring: 2, fuzzy: 1 } as const;
/** A keyword match counts this much less than the same match in the title. */
const KEYWORD_PENALTY = 0.5;

/**
 * Lower case without diacritics, keeping a map from each character of the result
 * back to its index in the original — so a match found in the folded text can be
 * drawn bold in the text as written.
 */
export function fold(text: string): { folded: string; origin: number[] } {
  let folded = "";
  const origin: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const plain = text[index]!.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    for (const char of plain) {
      folded += char;
      origin.push(index);
    }
  }
  return { folded, origin };
}

export const normalizeQuery = (query: string): string => fold(query.trim().replace(/\s+/g, " ")).folded;

const isWordStart = (text: string, index: number): boolean => index === 0 || /[^\p{L}\p{N}]/u.test(text[index - 1]!);

/** The best tier `query` (already folded) reaches in `text`, with the matched indices of the original. */
export function matchText(text: string, query: string): { score: number; positions: number[] } | null {
  if (!query) return null;
  const { folded, origin } = fold(text);
  const span = (start: number, length: number) => {
    const positions = new Set<number>();
    for (let i = start; i < start + length; i += 1) positions.add(origin[i]!);
    return [...positions];
  };
  // A small bonus for shorter text, so "Status" beats "Status of the deploy" in its tier.
  const tidy = (tier: number) => tier + 1 / (10 + folded.length);
  if (folded === query) return { score: tidy(TIER.exact), positions: span(0, query.length) };
  if (folded.startsWith(query)) return { score: tidy(TIER.prefix), positions: span(0, query.length) };
  let at = folded.indexOf(query);
  const first = at;
  while (at > 0 && !isWordStart(folded, at)) at = folded.indexOf(query, at + 1);
  if (at > 0) return { score: tidy(TIER.word), positions: span(at, query.length) };
  // One letter matches only where a word starts: "a" mid-word is in nearly everything.
  if (query.length === 1) return null;
  if (first >= 0) return { score: tidy(TIER.substring), positions: span(first, query.length) };
  // Fuzzy: every character of the query in order (spaces in it ignored), each one
  // starting a word or following the last — "sttp" finds "Sta tus page", "gh l" "GitHub
  // Light" — but not letters scattered mid-word, which matched nearly anything.
  const wanted = query.replace(/ /g, "");
  const hits: number[] = [];
  let from = 0;
  for (const char of wanted) {
    let found = folded.indexOf(char, from);
    const previous = hits[hits.length - 1];
    while (found >= 0 && found !== (previous ?? -2) + 1 && !isWordStart(folded, found)) found = folded.indexOf(char, found + 1);
    if (found < 0) return null;
    hits.push(found);
    from = found + 1;
  }
  if (hits.length === 0) return null;
  // Tighter runs rank higher within the tier.
  const spread = hits[hits.length - 1]! - hits[0]! + 1 - hits.length;
  return { score: TIER.fuzzy + 1 / (2 + spread), positions: [...new Set(hits.map((i) => origin[i]!))] };
}

/** One item against a folded query: its title, else its keywords (less), else its fallback. */
export function scoreItem<T>(item: PaletteItem<T>, query: string): Ranked<T> | null {
  const title = matchText(item.title, query);
  let best: Ranked<T> | null = title ? { item, score: title.score, positions: title.positions } : null;
  for (const keyword of item.keywords) {
    const hit = matchText(keyword, query);
    if (hit && (!best || hit.score - KEYWORD_PENALTY > best.score)) {
      best = { item, score: hit.score - KEYWORD_PENALTY, positions: [] };
    }
  }
  if (!best && item.fallbackScore !== undefined) best = { item, score: item.fallbackScore, positions: [] };
  return best;
}

/** Every item the query matches, best first; ties to the more recently chosen, then by kind. */
export function rank<T>(items: PaletteItem<T>[], query: string, recent: string[] = []): Ranked<T>[] {
  const folded = normalizeQuery(query);
  if (!folded) return [];
  const recency = new Map(recent.map((id, index) => [id, index]));
  const order = new Map(items.map((item, index) => [item, index]));
  const out: Ranked<T>[] = [];
  for (const item of items) {
    const hit = scoreItem(item, folded);
    if (hit) out.push(hit);
  }
  return out.sort(
    (a, b) =>
      b.score - a.score ||
      (recency.get(a.item.id) ?? Infinity) - (recency.get(b.item.id) ?? Infinity) ||
      KIND_ORDER.indexOf(a.item.kind) - KIND_ORDER.indexOf(b.item.kind) ||
      order.get(a.item)! - order.get(b.item)!,
  );
}

/**
 * What the palette lists. No query: Recent (what was chosen last, still in the
 * index and not archived), then every page not already there. A query: one group
 * per kind, each capped, the groups ordered by their best match — so the best
 * match overall is always the first row.
 */
export function groupResults<T>(items: PaletteItem<T>[], query: string, recent: string[] = []): PaletteGroup<T>[] {
  if (!normalizeQuery(query)) {
    const byId = new Map(items.map((item) => [item.id, item]));
    const recentItems = recent
      .map((id) => byId.get(id))
      .filter((item): item is PaletteItem<T> => Boolean(item && !item.archived))
      .slice(0, RECENT_SHOWN);
    const shown = new Set(recentItems.map((item) => item.id));
    const groups: PaletteGroup<T>[] = [];
    if (recentItems.length) {
      groups.push({ key: "recent", label: GROUP_LABELS.recent, results: recentItems.map((item) => ({ item, score: 0, positions: [] })) });
    }
    const pages = items.filter((item) => item.kind === "page" && !shown.has(item.id));
    if (pages.length) groups.push({ key: "page", label: GROUP_LABELS.page, results: pages.map((item) => ({ item, score: 0, positions: [] })) });
    return groups;
  }
  const byKind = new Map<PaletteKind, Ranked<T>[]>();
  for (const hit of rank(items, query, recent)) {
    const list = byKind.get(hit.item.kind) ?? [];
    if (list.length < GROUP_CAPS[hit.item.kind]) list.push(hit);
    byKind.set(hit.item.kind, list);
  }
  return [...byKind.entries()]
    .map(([kind, results]) => ({ key: kind, label: GROUP_LABELS[kind], results }))
    .sort(
      (a, b) =>
        b.results[0]!.score - a.results[0]!.score || KIND_ORDER.indexOf(a.key as PaletteKind) - KIND_ORDER.indexOf(b.key as PaletteKind),
    );
}

/** The recent list after choosing `id`: it first, no repeats, at most `RECENT_LIMIT`. */
export const pushRecent = (recent: string[], id: string): string[] => [id, ...recent.filter((other) => other !== id)].slice(0, RECENT_LIMIT);

export function parseRecent(raw: string | null): string[] {
  try {
    const value = JSON.parse(raw ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

/** Splits `text` into runs, `bold` where a position matched. */
export function highlightRuns(text: string, positions: number[]): Array<{ text: string; bold: boolean }> {
  if (!positions.length) return [{ text, bold: false }];
  const marked = new Set(positions);
  const runs: Array<{ text: string; bold: boolean }> = [];
  for (let index = 0; index < text.length; index += 1) {
    const bold = marked.has(index);
    const last = runs[runs.length - 1];
    if (last && last.bold === bold) last.text += text[index];
    else runs.push({ text: text[index]!, bold });
  }
  return runs;
}

/** "⌘K" on an Apple device, "Ctrl K" elsewhere. */
export const isApplePlatform = (platform: string): boolean => /mac|iphone|ipad|ipod/i.test(platform);

/** Whether a keydown is the palette's shortcut: ⌘K on a Mac, Ctrl+K elsewhere (either is taken on both). */
export const isPaletteShortcut = (event: Pick<KeyboardEvent, "key" | "code" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): boolean =>
  (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && (event.code === "KeyK" || event.key.toLowerCase() === "k");
